import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Card, Form, Col, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { NumericFormat as NumberFormat } from "react-number-format";
import Rating from "react-rating";
import Select from "react-select";

import Address from "../../../layout/address/jurisdiction";
import { deepEqual } from "../../../utils/object";
import { format as formatDate } from "../../../utils/date";
import { ConnectedSettingList } from "../../../layout/select/setting-list";
import RateEditor from "./rate-editor";

const mapPropsToState = (props) => ({
  primaryArea: props.primaryArea ?? "",
  secondaryArea: props.secondaryArea ?? "",
  status: props.status ?? "OPEN",
  stage: props.stage ?? 0,
  nextFollowUpAt: props.nextFollowUpAt ?? null,
  lastContactedAt: props.lastContactedAt ?? null,
  lastActivityAt: props.lastActivityAt ?? null,
  probability: props.probability ?? 0,
  estimatedValue: props.estimatedValue ?? 0,
  lostReason: props.lostReason ?? "",
  convertedAt: props.convertedAt ?? null,
  convertedClientUuid: props.convertedClientUuid ?? null,
  assignedToUuid: props.assignedToUuid ?? null,
  meta: props.meta && typeof props.meta === "object" ? { ...props.meta } : {},
  source: props.source ?? "unsourced",
  timeline: props.timeline ?? 0,
  intent: props.intent ?? 1,
  fit: props.fit ?? 1,
  CaseLocation:
    props.CaseLocation && typeof props.CaseLocation === "object"
      ? { ...props.CaseLocation }
      : {},
  subject: props.subject ?? "",
  retainer: props.retainer ?? 0,
  flat: props.flat ?? 0,
  minutes: props.minutes ?? 6,
  rates: Array.isArray(props.rates) ? props.rates : [],
});

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Open" },
  { value: "HIRED", label: "Hired" },
  { value: "NOT_HIRED", label: "Not Hired" },
  { value: "ARCHIVED", label: "Archived" },
];

const selectedStatusOption = (status) => (
  STATUS_OPTIONS.find((option) => option.value === status) ||
  STATUS_OPTIONS[0]
);

const ACTIVITY_OPTIONS = [
  { value: "NOTE", label: "Note" },
  { value: "CALL", label: "Call" },
  { value: "EMAIL", label: "Email" },
  { value: "SMS", label: "SMS" },
  { value: "MEETING", label: "Meeting" },
  { value: "TASK", label: "Task" },
  { value: "FOLLOW_UP", label: "Follow Up" },
];

const activityLabel = (type) => ACTIVITY_OPTIONS.find((item) => item.value === type)?.label || type || "";

const Lead = forwardRef(function Lead(props, ref) {
  const { uuid, getLeadActivities, completeLeadActivity, getStaff } = props;
  const [lead, setLead] = useState(() => mapPropsToState(props));
  const [activities, setActivities] = useState([]);
  const [staff, setStaff] = useState([]);

  const isHydratingRef = useRef(true);
  const initialValueRef = useRef(mapPropsToState(props));

  const mappedProps = useMemo(
    () => mapPropsToState(props),
    [
      props.primaryArea,
      props.secondaryArea,
      props.status,
      props.stage,
      props.nextFollowUpAt,
      props.lastContactedAt,
      props.lastActivityAt,
      props.probability,
      props.estimatedValue,
      props.lostReason,
      props.convertedAt,
      props.convertedClientUuid,
      props.assignedToUuid,
      props.meta,
      props.source,
      props.timeline,
      props.intent,
      props.fit,
      props.CaseLocation,
      props.subject,
      props.retainer,
      props.flat,
      props.minutes,
      props.rates,
    ]
  );

  const prevMappedPropsRef = useRef(mappedProps);

  const loadActivities = useCallback(async () => {
    if (!uuid || !getLeadActivities) return;
    const result = await getLeadActivities({ leadUuid: uuid });
    setActivities(result?.items || []);
  }, [uuid, getLeadActivities]);

  const loadStaff = useCallback(async () => {
    if (!getStaff) return;
    const result = await getStaff({});
    setStaff((result || []).map((item) => ({
      ...item,
      value: item.uuid,
      label: item.Person?.displayName || [item.Person?.firstName, item.Person?.lastName].filter(Boolean).join(" ") || item.number || item.uuid,
    })));
  }, [getStaff]);

  useEffect(() => {
    isHydratingRef.current = false;
  }, []);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    const prevMapped = prevMappedPropsRef.current;
    if (deepEqual(prevMapped, mappedProps)) return;

    isHydratingRef.current = true;
    setLead(mappedProps);
    prevMappedPropsRef.current = mappedProps;
    initialValueRef.current = mappedProps;

    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [mappedProps]);

  const updateField = useCallback((name, value) => {
    if (!name) return;
    setLead((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const onChange = useCallback(
    (e) => {
      const name = e?.target?.name;
      const value = e?.target?.value;
      updateField(name, value);
    },
    [updateField]
  );

  const onChangeIndex = useCallback(
    (e) => {
      const name = e?.target?.name;
      const index = e?.target?.index;
      updateField(name, index);
    },
    [updateField]
  );

  const onNumberChange = useCallback(
    (e, name) => {
      updateField(name, e?.floatValue ?? 0);
    },
    [updateField]
  );

  const onRatingChange = useCallback(
    (name, value) => {
      updateField(name, value);
    },
    [updateField]
  );

  const onStatusChange = useCallback((option) => {
    updateField("status", option?.value || "OPEN");
  }, [updateField]);

  const onAssignedToChange = useCallback((option) => {
    updateField("assignedToUuid", option?.value || null);
  }, [updateField]);

  const onCompleteActivity = useCallback(async (item) => {
    if (!completeLeadActivity || !item?.uuid) return;
    await completeLeadActivity({ uuid: item.uuid });
    await loadActivities();
  }, [loadActivities, completeLeadActivity]);

  const onEditActivity = useCallback((item) => {
    if (props.onEditLeadActivity) props.onEditLeadActivity(item);
  }, [props]);

  const onDeleteActivity = useCallback((item) => {
    if (props.onDeleteLeadActivity) props.onDeleteLeadActivity(item);
  }, [props]);

  const hasOpenFollowUp = useCallback(() => (
    (activities || []).some((item) => !item.completedAt && ["TASK", "FOLLOW_UP"].includes(item.type))
  ), [activities]);

  const onRateChange = useCallback(
    (rates) => {
      updateField("rates", Array.isArray(rates) ? rates : []);
    },
    [updateField]
  );

  const onAddressChange = useCallback(
    (objectName, values) => {
      updateField(objectName, { ...(values || {}) });
    },
    [updateField]
  );

  const validate = useCallback(() => {
    const errors = {};

    if (!String(lead.primaryArea ?? "").trim()) {
      errors.primaryArea = "Primary practice is required.";
    }

    if (!String(lead.source ?? "").trim()) {
      errors.source = "Source is required.";
    }

    if (Number(lead.timeline ?? 0) < 0) {
      errors.timeline = "Timeline cannot be negative.";
    }

    if (Number(lead.retainer ?? 0) < 0) {
      errors.retainer = "Initial retainer cannot be negative.";
    }

    if (Number(lead.flat ?? 0) < 0) {
      errors.flat = "Flat fee cannot be negative.";
    }

    if (Number(lead.minutes ?? 0) <= 0) {
      errors.minutes = "Interval must be greater than zero.";
    }

    return {
      success: Object.keys(errors).length === 0,
      errors,
    };
  }, [lead]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => ({
        ...lead,
        CaseLocation: { ...(lead.CaseLocation || {}) },
        rates: Array.isArray(lead.rates) ? [...lead.rates] : [],
      }),
      setValue: (value = {}) => {
        const next = mapPropsToState(value);
        setLead(next);
      },
      reset: (value = null) => {
        const next = value ? mapPropsToState(value) : initialValueRef.current;
        setLead(next);
      },
      loadActivities,
      hasOpenFollowUp,
      validate,
      isDirty: () => !deepEqual(initialValueRef.current, lead),
    }),
    [lead, loadActivities, hasOpenFollowUp, validate]
  );

  const hasActivities = (activities || []).length > 0;
  const activityList = hasActivities ? (
    <Row className="g-3">
      <Col lg="12" className="d-flex align-items-center justify-content-between">
        <h2 className="small-title mb-0">Lead Activity</h2>
        <span className="text-muted small">{(activities || []).length} items</span>
      </Col>
      <Col xs="12">
        <div className="border-top">
          {activities.map((item) => (
            <div key={item.uuid} className="py-2 border-bottom d-flex justify-content-between gap-3">
              <div>
                <span className={`badge me-2 ${item.completedAt ? "bg-success" : item.dueAt ? "bg-warning text-dark" : "bg-primary"}`}>
                  {item.completedAt ? "Done" : activityLabel(item.type)}
                </span>
                <strong>{item.subject || activityLabel(item.type)}</strong>
                {item.dueAt ? <span className="text-muted ms-2">Due {formatDate(item.dueAt, true)}</span> : null}
                {item.assignedToUuid ? (
                  <span className="text-muted ms-2">
                    Assigned to {item.AssignedTo?.displayName || [item.AssignedTo?.firstName, item.AssignedTo?.lastName].filter(Boolean).join(" ") || item.AssignedTo?.email || "staff"}
                  </span>
                ) : null}
                <div className="text-muted small">{item.body || ""}</div>
              </div>
              <div className="d-flex align-items-start gap-2">
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onEditActivity(item)}>
                  Edit
                </button>
                {!item.completedAt && ["TASK", "FOLLOW_UP"].includes(item.type) ? (
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onCompleteActivity(item)}>
                    Complete
                  </button>
                ) : null}
                <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => onDeleteActivity(item)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </Col>
    </Row>
  ) : null;

  const convertedMatterUuid = lead.meta?.convertedMatterUuid;

  return (
    <>
      <Card body className="mb-3">
      <Col xs="12">
        <Row className="g-3 align-items-center">
          <Col lg="12">
            <h2 className="small-title">Representation Information</h2>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="6">
            <div className="mb-3 top-label">
              <ConnectedSettingList
                name="primaryArea"
                setting="LEAD_AREA"
                category="Collections"
                value={lead.primaryArea ?? ""}
                onChange={onChange}
              />
              <Form.Label>PRIMARY PRACTICE</Form.Label>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3 top-label">
              <ConnectedSettingList
                name="secondaryArea"
                setting="LEAD_AREA"
                category="Collections"
                value={lead.secondaryArea ?? ""}
                onChange={onChange}
              />
              <Form.Label>SECONDARY PRACTICE</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="3">
            <div className="mb-3 top-label">
              <Select
                inputId="leadStatus"
                classNamePrefix="react-select"
                options={STATUS_OPTIONS}
                value={selectedStatusOption(lead.status)}
                onChange={onStatusChange}
                isClearable={false}
              />
              <Form.Label>STATUS</Form.Label>
            </div>
          </Col>
          <Col md="3">
            <div className="mb-3 top-label">
              <ConnectedSettingList
                name="stage"
                setting="LEAD_STAGE"
                category="Collections"
                index={lead.stage ?? 0}
                onChange={onChangeIndex}
                indexed
              />
              <Form.Label>STAGE</Form.Label>
            </div>
          </Col>
          <Col md="3">
            <div className="mb-3 top-label">
              <ConnectedSettingList
                name="source"
                setting="LEAD_SOURCE"
                category="Collections"
                value={lead.source ?? ""}
                onChange={onChange}
              />
              <Form.Label>SOURCE</Form.Label>
            </div>
          </Col>
          <Col md="3">
            <div className="mb-3 top-label">
              <Select
                inputId="leadAssignedTo"
                classNamePrefix="react-select"
                options={staff}
                value={staff.find((option) => option.value === lead.assignedToUuid) || null}
                onChange={onAssignedToChange}
                isClearable
              />
              <Form.Label>ASSIGNED TO</Form.Label>
            </div>
          </Col>
        </Row>

        {lead.convertedClientUuid ? (
          <Row className="g-3">
            <Col xs="12">
              <div className="alert alert-success py-2 mb-0 d-flex flex-wrap align-items-center gap-2">
                <span>
                  Converted{lead.convertedAt ? ` ${formatDate(lead.convertedAt, true)}` : ""}.
                </span>
                <a href={`/dashboard/people/${lead.convertedClientUuid}`} className="alert-link">
                  Open client
                </a>
                {convertedMatterUuid ? (
                  <a href={`/dashboard/matters/${convertedMatterUuid}`} className="alert-link">
                    Open matter
                  </a>
                ) : null}
              </div>
            </Col>
          </Row>
        ) : null}

        <Row className="g-3">
          <Col md="3">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="probability"
                name="probability"
                className="text-end form-control mb-2"
                suffix="%"
                thousandSeparator={false}
                allowNegative={false}
                value={lead.probability ?? 0}
                onValueChange={(e) => onNumberChange(e, "probability")}
                isAllowed={({ floatValue }) => floatValue == null || floatValue <= 100}
              />
              <Form.Label>PROBABILITY</Form.Label>
            </div>
          </Col>
          <Col md="3">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="estimatedValue"
                name="estimatedValue"
                className="text-end form-control mb-2"
                prefix="$"
                thousandSeparator
                allowNegative={false}
                value={lead.estimatedValue ?? 0}
                onValueChange={(e) => onNumberChange(e, "estimatedValue")}
                isAllowed={({ floatValue }) => floatValue == null || floatValue <= 5000000}
              />
              <Form.Label>ESTIMATED VALUE</Form.Label>
            </div>
          </Col>
          <Col md="3">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="lostReason"
                value={lead.lostReason ?? ""}
                onChange={onChange}
                disabled={lead.status !== "NOT_HIRED"}
              />
              <Form.Label>NOT HIRED REASON</Form.Label>
            </div>
          </Col>
          <Col md="3">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                value={formatDate(lead.nextFollowUpAt)}
                disabled
              />
              <Form.Label>NEXT FOLLOW-UP</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="4">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="timeline"
                name="timeline"
                className="text-end form-control mb-2"
                thousandSeparator={false}
                allowNegative={false}
                value={lead.timeline ?? 0}
                onValueChange={(e) => onNumberChange(e, "timeline")}
                isAllowed={({ floatValue }) => floatValue == null || floatValue <= 120}
              />
              <Form.Label>TIMELINE (DAYS)</Form.Label>
            </div>
          </Col>

          <Col md="4">
            <div className="mb-3">
              <Form.Label>INTENT</Form.Label>
              <br />
              <Rating
                placeholderRating={lead.intent ?? 1}
                onChange={(e) => onRatingChange("intent", e)}
                start={1}
                stop={10}
                emptySymbol={<span style={{ fontSize: "1.1rem" }}>☆</span>}
                fullSymbol={<span style={{ fontSize: "1.1rem" }}>★</span>}
                placeholderSymbol={<span style={{ fontSize: "1.1rem" }}>★</span>}
              />
            </div>
          </Col>

          <Col md="4">
            <div className="mb-3">
              <Form.Label>FIT</Form.Label>
              <br />
              <Rating
                placeholderRating={lead.fit ?? 1}
                onChange={(e) => onRatingChange("fit", e)}
                start={1}
                stop={10}
                emptySymbol={<span style={{ fontSize: "1.1rem" }}>☆</span>}
                fullSymbol={<span style={{ fontSize: "1.1rem" }}>★</span>}
                placeholderSymbol={<span style={{ fontSize: "1.1rem" }}>★</span>}
              />
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg="12">
            <h2 className="small-title">Jurisdiction (Case/Incident Location)</h2>
          </Col>
        </Row>

        <Address
          {...lead?.CaseLocation}
          onChange={(values) => onAddressChange("CaseLocation", values)}
        />

        <Row className="g-3">
          <Col xl="6">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="subject"
                value={lead.subject ?? ""}
                onChange={onChange}
              />
              <Form.Label>SUBJECT (DESCRIBED)</Form.Label>
            </div>
          </Col>

          <Col md="4" xl="2">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="retainer"
                name="retainer"
                className="text-end form-control mb-2"
                prefix="$"
                thousandSeparator={false}
                allowNegative={false}
                value={lead.retainer ?? 0}
                onValueChange={(e) => onNumberChange(e, "retainer")}
                isAllowed={({ floatValue }) => floatValue == null || floatValue <= 500000}
              />
              <Form.Label>INITIAL RETAINER</Form.Label>
            </div>
          </Col>

          <Col md="4" xl="2">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="flat"
                name="flat"
                className="text-end form-control mb-2"
                thousandSeparator={false}
                prefix="$"
                allowNegative={false}
                value={lead.flat ?? 0}
                onValueChange={(e) => onNumberChange(e, "flat")}
                isAllowed={({ floatValue }) => floatValue == null || floatValue <= 5000000}
              />
              <Form.Label>FLAT FEE</Form.Label>
            </div>
          </Col>

          <Col md="4" xl="2">
            <div className="mb-3 top-label">
              <NumberFormat
                displayType="input"
                type="text"
                id="minutes"
                name="minutes"
                className="text-end form-control mb-2"
                thousandSeparator={false}
                allowNegative={false}
                value={lead.minutes ?? 6}
                onValueChange={(e) => onNumberChange(e, "minutes")}
                isAllowed={({ floatValue }) => floatValue == null || floatValue <= 60}
              />
              <Form.Label>INTERVAL (MINUTES)</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col>
            <h2 className="small-title">Quoted Rates</h2>
          </Col>
        </Row>

        <RateEditor onChange={onRateChange} maxValue={50000} rates={lead.rates} />

      </Col>
      </Card>
      {activityList ? (
        <Card body className="mb-5">
          <Col xs="12">
            {activityList}
          </Col>
        </Card>
      ) : null}
    </>
  );
});

export default Lead;
